import { EVENTS } from '@archetype-themes/utils/events'
import { debounce } from '@archetype-themes/utils/utils'

class PredictiveSearch extends HTMLElement {
  constructor() {
    super()
    this.enabled = this.getAttribute('data-enabled')
    this.context = this.getAttribute('data-context')
    this.input = this.querySelector('input[type="search"]')
    this.predictiveSearchResults = this.querySelector('#predictive-search')
    this.closeBtn = this.querySelector('.btn--close-search')
    this.searchBtn = this.querySelector('.btn--search')
    this.screen = this.querySelector('[data-screen]')
    this.SearchModal = this.closest('#SearchModal') || null
    
    // Store original search button HTML for restoration
    this.originalSearchBtnHTML = this.searchBtn ? this.searchBtn.innerHTML : null
    
    // Cache for popular items content
    this.popularItemsCache = null
    this.popularItemsLoading = false

    // listen for class change of 'modal--is-active on this.SearchModal
    if (this.SearchModal) {
      const observer = new MutationObserver((mutations) => {
        mutations.forEach((mutation) => {
          if (mutation.attributeName === 'class') {
            const modalClass = mutation.target.className
            if (modalClass.indexOf('modal--is-active') > -1) {
              setTimeout(() => {
                this.input.focus()
              }, 100)
            }
          }
        })
      })

      observer.observe(this.SearchModal, {
        attributes: true
      })
    }
  }

  connectedCallback() {
    if (this.enabled === 'false') return

    this.abortController = new AbortController()
    
    // Fetch popular items on page load for caching
    this.fetchPopularItems()
    
    // Get search input wrap element
    this.searchInputWrap = this.querySelector('[data-search-input-wrap]')
    this.isSearchExpanded = false

    // Initialize search button behavior
    if (this.searchBtn) {
      this.searchBtn.type = 'button'
      this.searchBtn.removeAttribute('onclick')
      
      // Add click handler for search button
      this.searchBtn.addEventListener('click', (e) => {
        e.preventDefault()
        e.stopPropagation()
        this.toggleSearchInput()
      }, { signal: this.abortController.signal })
    }

    // Handle clicks outside to close search input and results
    document.addEventListener('click', (e) => {
      // Check if search is expanded or if predictive search is active
      if ((this.isSearchExpanded || this.classList.contains('is-active')) && !this.contains(e.target)) {
        // Check if it's not a search trigger button
        const isSearchTrigger = e.target.closest('.site-nav__link--icon[href*="search"]') || 
                               e.target.closest('[data-search-toggle]')
        
        if (!isSearchTrigger) {
          this.collapseSearchInput()
        }
      }
    }, { signal: this.abortController.signal })

    // Show search input on mobile and desktop when needed
    this.initializeSearchDisplay()

    // Handle resize events to adjust behavior between mobile and desktop
    window.addEventListener('resize', () => {
      this.initializeSearchDisplay()
    }, { signal: this.abortController.signal })

    // Open events
    document.addEventListener(
      EVENTS.predictiveSearchOpen,
      (e) => {
        if (e.detail.context !== this.context) return
        this.classList.add('is-active')
        
        this.input.focus()
        // Show popular items when search opens with empty input
        if (!this.input.value.trim()) {
          this.showPopularItems()
        }

        document.body.classList.add('predictive-overflow-hidden')
      },
      { signal: this.abortController.signal }
    )

    // On typing
    this.input.addEventListener('keydown', () => {
      this.classList.add('is-active')
    })

    this.input.addEventListener(
      'input',
      debounce(300, (event) => {
        this.onChange(event)
      }).bind(this)
    )

    this.input.addEventListener(
      'focus',
      () => {
        this.toggleSearchIcon(true)
        const searchTerm = this.input.value.trim()
        if (searchTerm.length > 0) {
          // If there's text in the input, show predictive search
          this.classList.add('is-active')
          this.getSearchResults(searchTerm)
        } else {
          // If input is empty, show popular items
          this.classList.add('is-active')
          this.showPopularItems()
          this.open()
        }
      },
      { signal: this.abortController.signal }
    )

    // Add blur event to revert icon when input loses focus
    this.input.addEventListener(
      'blur',
      () => {
        // Only revert icon if search is not active
        setTimeout(() => {
          if (!this.classList.contains('is-active')) {
            this.toggleSearchIcon(false)
          }
        }, 100)
      },
      { signal: this.abortController.signal }
    )

    // Close events
    document.addEventListener(
      EVENTS.predictiveSearchClose,
      () => {
        this.collapseSearchInput()
      },
      { signal: this.abortController.signal }
    )

    document.addEventListener(
      'keydown',
      (evt) => {
        if (evt.keyCode === 27) this.collapseSearchInput()
      },
      { signal: this.abortController.signal }
    )

    this.closeBtn?.addEventListener(
      'click',
      (evt) => {
        evt.preventDefault()
        this.collapseSearchInput()
      },
      { signal: this.abortController.signal }
    )

    // Add specific handler for screen overlay clicks
    this.screen?.addEventListener(
      'click',
      () => {
        this.collapseSearchInput()
      },
      { signal: this.abortController.signal }
    )

    document.addEventListener(EVENTS.headerSearchClose, () => {
      this.collapseSearchInput()
    })
  }

  disconnectedCallback() {
    this.abortController.abort()
  }

  onChange() {
    const searchTerm = this.input.value.trim()
    if (!searchTerm.length) {
      this.showPopularItems()
      return
    }

    this.getSearchResults(searchTerm)
  }

  getSearchResults(searchTerm) {
    const searchObj = {
      q: searchTerm,
      'resources[limit]': 4,
      'resources[limit_scope]': 'each',
      'resources[options][unavailable_products]': 'last'
    }

    const params = this.paramUrl(searchObj)

    this.open()

    fetch(`${window.Shopify.routes.root}search/suggest?${params}&section_id=search-results`)
      .then((response) => {
        if (!response.ok) {
          const error = new Error(response.status)
          this.close()
          throw error
        }

        return response.text()
      })
      .then((text) => {
        const resultsMarkup = new DOMParser()
          .parseFromString(text, 'text/html')
          .querySelector('#shopify-section-search-results').innerHTML
        this.predictiveSearchResults.innerHTML = resultsMarkup
        
        // Update product count after results are loaded
        this.updateProductCount()
      })
      .catch((error) => {
        this.close()
        throw error
      })
  }

  open() {
    // Force a reflow to ensure display change is applied before animation
    this.predictiveSearchResults.offsetHeight
  }

  close() {
    // Remove active class first to trigger CSS animation
    this.classList.remove('is-active')

    document.body.classList.remove('predictive-overflow-hidden')
    if (this.contains(document.activeElement)) document.activeElement.blur()

    // Revert search icon when closing
    this.toggleSearchIcon(false)

    /**
     * @event predictive-search:close-all
     * @description Fired when the predictive search is closed.
     */
    this.dispatchEvent(new CustomEvent(EVENTS.predictiveSearchCloseAll, { bubbles: true }))
  }

  fetchPopularItems() {
    // Skip if already loaded or currently loading
    if (this.popularItemsCache || this.popularItemsLoading) return

    this.popularItemsLoading = true
    
    fetch(`${window.Shopify.routes.root}?section_id=popular-search-items`)
      .then((response) => {
        if (!response.ok) {
          throw new Error(response.status)
        }
        return response.text()
      })
      .then((text) => {
        const resultsMarkup = new DOMParser()
          .parseFromString(text, 'text/html')
          .querySelector('#shopify-section-popular-search-items').innerHTML
        this.popularItemsCache = resultsMarkup
        this.popularItemsLoading = false
      })
      .catch((error) => {
        console.error('Failed to load popular items:', error)
        this.popularItemsLoading = false
      })
  }

  showPopularItems() {
    this.classList.add('is-active')
    document.body.classList.add('predictive-overflow-hidden')
    
    this.open()
    
    // Use cached content if available
    if (this.popularItemsCache) {
      this.predictiveSearchResults.innerHTML = this.popularItemsCache
      return
    }
    
    // If still loading, wait for it to complete
    if (this.popularItemsLoading) {
      const checkCache = () => {
        if (this.popularItemsCache) {
          this.predictiveSearchResults.innerHTML = this.popularItemsCache
        } else if (this.popularItemsLoading) {
          setTimeout(checkCache, 50) // Check again in 50ms
        } else {
          // Loading failed, try to fetch again
          this.fetchPopularItems()
          setTimeout(checkCache, 100)
        }
      }
      checkCache()
      return
    }
    
    // Fallback: fetch if not cached and not loading
    this.fetchPopularItems()
  }

  updateProductCount() {
    // Count the actual product items in the results
    const productItems = this.predictiveSearchResults.querySelectorAll('.predictive-search-products-grid li[role="option"]')
    const productCount = productItems.length
    
    // Find and update the count element
    const countElement = this.predictiveSearchResults.querySelector('.results-products__count')
    if (countElement) {
      countElement.textContent = `(${productCount})`
    }
  }

  toggleSearchIcon(showClose) {
    if (!this.searchBtn) return

    if (showClose) {
      // Add close button styling
      this.searchBtn.classList.add('search__btn--close')
      
      // Change to close icon with white color
      this.searchBtn.innerHTML = `
        <svg
          xmlns="http://www.w3.org/2000/svg"
          aria-hidden="true"
          focusable="false"
          role="presentation"
          class="icon icon-close icon--line"
          viewBox="0 0 64 64"
          width="20"
          height="20"
          style="color: white;"
        >
          <path
            d="m19 17.61 27.12 27.13m0-27.13L19 44.74"
            stroke-miterlimit="10"
            fill="none"
            stroke="white!important"
          />
        </svg>
        <span class="icon__fallback-text visually-hidden">Close</span>
      `
      // Make search button act as close button
      this.searchBtn.type = 'button'
      this.searchBtn.onclick = (e) => {
        e.preventDefault()
        this.collapseSearchInput()
      }
    } else {
      // Remove custom styling
      this.searchBtn.classList.remove('search__btn--close')
      
      // Restore original search button HTML if available
      if (this.originalSearchBtnHTML) {
        this.searchBtn.innerHTML = this.originalSearchBtnHTML
      } else {
        // Fallback to default search icon
        this.searchBtn.innerHTML = `
          <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg" style="margin: 3px;">
            <path
              d="M19 19L13 13M1 8C1 8.91925 1.18106 9.82951 1.53284 10.6788C1.88463 11.5281 2.40024 12.2997 3.05025 12.9497C3.70026 13.5998 4.47194 14.1154 5.32122 14.4672C6.1705 14.8189 7.08075 15 8 15C8.91925 15 9.82951 14.8189 10.6788 14.4672C11.5281 14.1154 12.2997 13.5998 12.9497 12.9497C13.5998 12.2997 14.1154 11.5281 14.4672 10.6788C14.8189 9.82951 15 8.91925 15 8C15 7.08075 14.8189 6.1705 14.4672 5.32122C14.1154 4.47194 13.5998 3.70026 12.9497 3.05025C12.2997 2.40024 11.5281 1.88463 10.6788 1.53284C9.82951 1.18106 8.91925 1 8 1C7.08075 1 6.1705 1.18106 5.32122 1.53284C4.47194 1.88463 3.70026 2.40024 3.05025 3.05025C2.40024 3.70026 1.88463 4.47194 1.53284 5.32122C1.18106 6.1705 1 7.08075 1 8Z"
              stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" />
          </svg>
          <span class="icon__fallback-text visually-hidden">Search</span>
        `
      }
      // Restore search button functionality
      this.searchBtn.type = 'submit'
      this.searchBtn.onclick = null
    }
  }

  initializeSearchDisplay() {
    if (!this.searchInputWrap) return
    
    // Check if we're on mobile (screen width < 768px)
    const isMobile = window.matchMedia('(max-width: 767px)').matches
    
    if (isMobile) {
      // On mobile, always show the search input
      this.searchInputWrap.style.display = 'block'
      this.isSearchExpanded = true
    } else {
      // On desktop, hide the search input by default
      this.searchInputWrap.style.display = 'none'
      this.isSearchExpanded = false
    }
  }

  toggleSearchInput() {
    // Check if we're on mobile
    const isMobile = window.matchMedia('(max-width: 767px)').matches
    
    if (isMobile) {
      // On mobile, just trigger the existing search functionality
      this.input.focus()
      if (this.input.value.trim()) {
        this.getSearchResults(this.input.value.trim())
      } else {
        this.showPopularItems()
      }
      return
    }

    // Desktop behavior: expand/collapse
    if (this.isSearchExpanded) {
      this.collapseSearchInput()
    } else {
      this.expandSearchInput()
    }
  }

  expandSearchInput() {
    if (!this.searchInputWrap) return
    
    this.searchInputWrap.style.display = 'block'
    this.isSearchExpanded = true
    
    // Focus the input and trigger search functionality immediately
    this.input.focus()
    if (this.input.value.trim()) {
      this.getSearchResults(this.input.value.trim())
    } else {
      this.showPopularItems()
    }
  }

  collapseSearchInput() {
    // Check if we're on mobile
    const isMobile = window.matchMedia('(max-width: 767px)').matches
    
    // Always close the predictive search results first
    this.close()
    
    // On desktop, also hide the input field
    if (!isMobile && this.searchInputWrap) {
      this.searchInputWrap.style.display = 'none'
      this.isSearchExpanded = false
    }
  }

  paramUrl(obj) {
    return Object.keys(obj)
      .map(function (key) {
        return key + '=' + encodeURIComponent(obj[key])
      })
      .join('&')
  }
}

customElements.define('predictive-search', PredictiveSearch)
